import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, DatePicker, Form, Input, Radio } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useTRPC } from '@/trpc/react.ts'

type Values = { billingMonth?: Dayjs; held: boolean; reason: string }
export function GroupCollectionSettings({
  group,
  onClose,
  onSaved
}: {
  group: {
    id: number
    updatedAt: Date
    billingMonth?: string | null
    collectionHeldAt?: Date | null
    collectionHoldReason?: string | null
  }
  onClose: () => void
  onSaved: () => Promise<unknown>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [open, setOpen] = useState(true)
  const save = useMutation(trpc.bill.setGroupCollectionSettings.mutationOptions())
  return (
    <ModalForm<Values>
      name="group-collection-settings"
      open={open}
      onOpenChange={setOpen}
      title="归属月份 / 催收设置"
      layout="vertical"
      width="32rem"
      initialValues={{
        billingMonth: group.billingMonth ? dayjs(`${group.billingMonth}-01`) : undefined,
        held: !!group.collectionHeldAt,
        reason: group.collectionHoldReason ?? ''
      }}
      modalProps={{ destroyOnHidden: true, afterClose: onClose, mask: { closable: false } }}
      submitter={{ searchConfig: { submitText: '保存' }, submitButtonProps: { loading: save.isPending } }}
      onFinish={async (values) => {
        try {
          await save.mutateAsync({
            groupId: group.id,
            expectedUpdatedAt: group.updatedAt,
            billingMonth: values.billingMonth?.format('YYYY-MM') ?? null,
            held: values.held,
            reason: values.reason
          })
          await onSaved()
          setOpen(false)
          void message.success('账单组催收设置已更新')
          return true
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '保存失败')
          return false
        }
      }}
    >
      <Alert
        className="mb-4!"
        type="info"
        showIcon
        title="设置仅作用于当前账单组"
        description="暂缓用于标记本组暂停催收，不改动组内账单，不减免费用，也不影响登记收款。历史出单快照保持不变。"
      />
      <Form.Item name="billingMonth" label="归属月份">
        <DatePicker picker="month" className="w-full" />
      </Form.Item>
      <Form.Item name="held" label="催收状态" rules={[{ required: true }]}>
        <Radio.Group
          options={[
            { label: '正常催收', value: false },
            { label: '暂缓催收', value: true }
          ]}
        />
      </Form.Item>
      <Form.Item
        name="reason"
        label="处理原因"
        rules={[{ required: true, whitespace: true, message: '请填写处理原因' }]}
      >
        <Input.TextArea maxLength={1000} rows={3} />
      </Form.Item>
    </ModalForm>
  )
}
